import React from 'react';
import {
  Box,
  Checkbox,
  Typography
} from '@mui/material';
import {
  TreeView,
  TreeItem
} from '@mui/x-tree-view';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { DufEntityDto } from 'providers/api';

export interface Layer {
  guid: string;
  name?: string;
}

export interface Vertex {
  x: number;
  y: number;
  z: number;
}

export interface Entity {
  guid?: string;
  type?: string;
  name?: string;
  vertexList?: Vertex[];
}

interface LayerTreeNode {
  name: string;
  guid?: string;
  layer?: DufEntityDto;
  children?: LayerTreeNode[];
}

interface LayerSelectionTreeProps {
  availableLayers: LayerTreeNode[];
  modelEntities: Entity[];
  selectedLayerGuids: string[];
  setSelectedLayerGuids: React.Dispatch<React.SetStateAction<string[]>>;
}

const LayerSelectionTree: React.FC<LayerSelectionTreeProps> = ({
  availableLayers,
  modelEntities,
  selectedLayerGuids,
  setSelectedLayerGuids,
}) => {
  // Helper: recursively collect all guids in a node subtree
  const getAllGuids = (node: LayerTreeNode): string[] => {
    let guids = node.guid ? [node.guid] : [];
    if (node.children) {
      node.children.forEach((child) => {
        guids = guids.concat(getAllGuids(child));
      });
    }
    return guids;
  };

  // Helper: format vertex list to show max 3 coordinates + "..."
  const formatVertices = (vertices?: Vertex[]) => {
    if (!vertices || vertices.length === 0) return '';
    const displayed = vertices.slice(0, 3).map((v) => `(${v.x.toFixed(2)}, ${v.y.toFixed(2)}, ${v.z.toFixed(2)})`);
    if (vertices.length > 3) {
      displayed.push('...');
    }
    return displayed.join(' - ');
  };

  const renderTree = (node: LayerTreeNode) => {
    const entitiesInLayer = modelEntities.filter((e) => {
      if (!e.vertexList || e.vertexList.length === 0) return false;

      const entityLayerKey = e.name;
      const nodeLayerName = node.layer?.name;
      const nodeGuid = node.guid;

      return (
        (entityLayerKey && nodeLayerName && entityLayerKey === nodeLayerName) || (entityLayerKey && nodeGuid && entityLayerKey === nodeGuid)
      );
    });

    const totalVertices = entitiesInLayer.reduce(
      (sum, entity) => sum + (entity.vertexList?.length || 0),
      0,
    );

    const label = (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Checkbox
          checked={node.guid ? selectedLayerGuids.includes(node.guid) : false}
          onChange={() => {
            if (!node.guid) return;
            const allGuids = getAllGuids(node);
            setSelectedLayerGuids((prev) => {
              const isSelected = allGuids.every((g) => prev.includes(g));
              if (isSelected) {
                return prev.filter((g) => !allGuids.includes(g));
              }
              return Array.from(new Set([...prev, ...allGuids]));
            });
          }}
        />
        <Typography>
          {typeof node.name === 'string' && node.name
            ? node.name.replace(/\\/g, '/')
            : node.guid}
        </Typography>
      </Box>
    );

    return (
      <TreeItem key={node.guid || node.name} nodeId={node.guid || node.name} label={label}>
        <TreeItem
          nodeId={`${node.guid}-details`}
          label={(
            <Typography variant="body2" color="text.secondary" sx={{ pl: 4 }}>
              {entitiesInLayer.length}
              polyline(s),
              {totalVertices}
              vertex
              {totalVertices !== 1 ? 'es' : ''}
            </Typography>
          )}
        >
          {entitiesInLayer.map((entity, index) => (
            <TreeItem
              key={entity.guid || index}
              nodeId={`${node.guid}-entity-${index}`}
              label={(
                <Typography variant="caption" color="text.secondary" sx={{ pl: 6, pr: 1 }}>
                  {formatVertices(entity.vertexList)}
                </Typography>
              )}
            />
          ))}
        </TreeItem>

        {node.children && node.children.length > 0 && node.children.map((child) => renderTree(child))}
      </TreeItem>
    );
  };

  return (
    <TreeView
      defaultCollapseIcon={<ExpandMore />}
      defaultExpandIcon={<ChevronRight />}
      sx={{ flexGrow: 1, overflowY: 'auto', maxHeight: 600 }}
    >
      {availableLayers.map((layer) => renderTree(layer))}
    </TreeView>
  );
};

export default LayerSelectionTree;
